import React from "react";
import {useTranslation} from "react-i18next";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";

import {arrayAlphabeticalSort} from "../../../utils/helpers";
import {SubCompanyPropTypes} from "./FilterComponentPropTypes";
import {SelectField, FilterLabel} from "./FilterComponentStyles";

const SubCompanyFilter = ({
    value,
    onChange,
    company,
    companies,
    city,
    disabled = true,
    initialCall = false,
    mode = undefined,
    showAllOption = true,
    allowClear = false,
    showSearch = false,
    hideTitle = false,
    selectMarginBottom = "20px",
    activity = "enabled", // all, enabled, disabled
    optionFilter = () => true,
    ...props
}: SubCompanyPropTypes) => {
    const {t} = useTranslation();
    const stores = useStore();

    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: company,
            companies: companies?.join(".") || "",
            limit: 1000,
            city: !city || city === "all" ? undefined : city,
            activity: activity
        }),
        dependencies: [company, companies, city],
        initialCall: initialCall,
        condition: company !== "pool"
    });

    const filteredSubCompanies = subCompanies?.rows?.filter(optionFilter) || [];

    const hasValueInOptions = filteredSubCompanies.some(subCompany => subCompany._id === value);
    const displayValue = hasValueInOptions ? value : undefined;

    return (
        <React.Fragment>
            {!hideTitle && <FilterLabel>{t("fields.subcompany", {ns: "common"})}</FilterLabel>}
            <SelectField
                // loading={subCompaniesLoading}
                data-testid="subCompanySelect"
                value={displayValue}
                onChange={onChange}
                disabled={
                    disabled &&
                    (!company || company === "all" || company === "pool" || subCompaniesLoading)
                }
                mode={mode}
                filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                allowClear={allowClear}
                showSearch={showSearch}
                mb={selectMarginBottom}
                {...props}
            >
                {showAllOption && (
                    <SelectField.Option value="all" data-testid="option">
                        {t("filters.all", {ns: "common"})}
                    </SelectField.Option>
                )}
                {filteredSubCompanies.length > 0 &&
                    arrayAlphabeticalSort([...filteredSubCompanies], "name").map(subCompany => (
                        <SelectField.Option
                            key={subCompany._id}
                            value={subCompany._id}
                            data-testid="subCompanySelect_option"
                        >
                            {subCompany.name}
                        </SelectField.Option>
                    ))}
            </SelectField>
        </React.Fragment>
    );
};

export default SubCompanyFilter;
